import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventDay } from '../../lib/eventStats';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { interactiveRow } from '../../components/a11y';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { StatusChip, DateChip, formatEventType } from './shared';

/*
 * Job page (mock: job-detail.html): one engagement — its activations,
 * shared gallery, client, deal facts. The topbar's Jobs / <job> picker
 * lives in the chrome; this page owns the title.
 */

export default function JobV2() {
    const { jobId } = useParams();
    const id = jobId ? +jobId : 0;
    const navigate = useNavigate();
    const { getJob, updateJob, addJobActivation } = useProduction();
    const { getOrgDomain } = useWhitelabelSettings();
    const { org } = useUserOrg();

    const [galleryBase, setGalleryBase] = useState('');
    useEffect(() => {
        let mounted = true;
        getOrgDomain(org?.id)
            .then((base: string) => mounted && setGalleryBase(base ?? ''))
            .catch(() => {});
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);

    const [job, setJob] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        const data = await getJob(id).catch(() => null);
        setJob(data);
        setLoading(false);
    };

    useEffect(() => {
        setLoading(true);
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading)
        return (
            <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="ps-label mb-3.5">Jobs · Job</div>
                <div>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                        />
                    ))}
                </div>
            </main>
        );

    if (!job)
        return (
            <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="ps-label mb-3.5">Jobs · Job</div>
                <div className="py-10 text-[13px] text-faint">
                    Job not found.
                </div>
            </main>
        );

    const activations: any[] = job.activations ?? [];
    const contacts: any[] = job.contacts ?? [];
    const startAt = activations.find((a) => a.startAt)?.startAt;
    const endAt = [...activations].reverse().find((a) => a.endAt ?? a.startAt);
    const totalCrew = activations.reduce(
        (sum, a) => sum + +(a.staffCount ?? 0),
        0,
    );
    const days =
        startAt && endAt
            ? eventDay(endAt.endAt ?? endAt.startAt).diff(
                  eventDay(startAt),
                  'days',
              ) + 1
            : null;

    const dateRange = startAt
        ? `${eventDay(startAt).format('MMM D')}${
              days && days > 1
                  ? ` – ${eventDay(endAt.endAt ?? endAt.startAt).format('MMM D')}`
                  : ''
          }`
        : 'Dates TBD';

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4 pb-8">
            <div className="ps-label mb-3">Jobs · Job</div>

            <div className="mb-6 flex flex-wrap items-start gap-4">
                <div className="min-w-0">
                    {editing ? (
                        <input
                            autoFocus
                            className="ps-input w-[420px] text-[20px] font-semibold"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            onKeyDown={async (e) => {
                                if (e.key === 'Escape') setEditing(false);
                                if (e.key === 'Enter' && draftName.trim()) {
                                    setBusy(true);
                                    await updateJob(id, {
                                        name: draftName.trim(),
                                    }).catch(() => null);
                                    setBusy(false);
                                    setEditing(false);
                                    await reload();
                                }
                            }}
                        />
                    ) : (
                        <h1 className="m-0 text-[23px] font-bold tracking-[-0.02em]">
                            {job.name}
                        </h1>
                    )}
                    <div className="mt-1 text-[12.5px] text-muted-foreground">
                        {formatEventType(
                            activations[0]?.eventType ?? job.typeName,
                        )}
                        {' · '}
                        {dateRange}
                        {job.attioDealId && (
                            <>
                                {' · '}
                                <a
                                    className="font-semibold text-brand-ink"
                                    href={`https://app.attio.com/smilebooth/deals/record/${job.attioDealId}/overview`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    deal in Attio ↗
                                </a>
                            </>
                        )}
                    </div>
                </div>
                <div className="ml-auto flex gap-1.5">
                    <button
                        type="button"
                        disabled={busy}
                        onClick={async () => {
                            setBusy(true);
                            const created = await addJobActivation(id).catch(
                                () => null,
                            );
                            setBusy(false);
                            if (created?.id)
                                navigate(`/v2/activations/${created.id}`);
                        }}
                        className="cursor-pointer rounded-full border-0 bg-foreground px-4 py-2 font-[inherit] text-[12px] font-semibold text-background disabled:opacity-50"
                    >
                        {busy ? 'Working…' : '+ Add activation'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setDraftName(job.name);
                            setEditing((e) => !e);
                        }}
                        className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-muted-foreground hover:text-foreground"
                    >
                        {editing ? 'Cancel' : 'Edit'}
                    </button>
                </div>
            </div>

            {/* stats */}
            <div className="mb-8 flex flex-wrap gap-7">
                {[
                    [String(activations.length), activations.length === 1 ? 'activation' : 'activations'],
                    days ? [String(days), days === 1 ? 'day' : 'days'] : null,
                    [String(totalCrew), 'crew assigned'],
                    job.billingEntity ? [job.billingEntity, 'billing entity'] : null,
                ]
                    .filter(Boolean)
                    .map(([n, l]: any) => (
                        <span key={l}>
                            <span className="block text-[19px] font-semibold tracking-[-0.02em] tabular-nums">
                                {n}
                            </span>
                            <span className="mt-px block text-[11px] text-faint">
                                {l}
                            </span>
                        </span>
                    ))}
            </div>

            {/* activations */}
            <section className="mb-9">
                <div className="ps-label mb-1.5">Activations</div>
                {activations.map((a: any) => (
                    <div
                        key={a.activationId}
                        {...interactiveRow(() =>
                            navigate(`/v2/activations/${a.activationId}`),
                        )}
                        className="flex cursor-pointer items-center gap-3.5 border-t border-line px-0.5 py-[11px] text-[12.5px] first:border-t-0 hover:bg-sunk"
                    >
                        <DateChip
                            date={a.startAt}
                            tbd={a.isProvisional}
                        />
                        <span className="min-w-0 flex-1">
                            <span className="block truncate font-semibold tracking-[-0.01em]">
                                {a.name}
                            </span>
                            <span className="mt-px block truncate text-[12px] text-muted-foreground">
                                {[a.venueName, a.eventName]
                                    .filter(Boolean)
                                    .join(' · ') || 'No venue yet'}
                            </span>
                        </span>
                        <StatusChip status={a.status} />
                        <span className="w-[86px] flex-none tabular-nums text-muted-foreground">
                            {+(a.staffCount ?? 0)} crew
                        </span>
                        <span className="flex-none text-faint">→</span>
                    </div>
                ))}
            </section>

            {/* shared gallery */}
            <section className="mb-9">
                <div className="ps-label mb-2">Shared gallery</div>
                {job.gallery ? (
                    <div className="flex max-w-[560px] items-center gap-4 rounded-2xl border border-border p-4">
                        <span className="min-w-0 flex-1">
                            <span className="block text-[13px] font-semibold">
                                {job.gallery.name ?? job.gallery.slug}
                            </span>
                            <span className="mt-px block text-[11.5px] text-faint">
                                one gallery across all activations
                                {job.gallery.slug && galleryBase
                                    ? ` · ${galleryBase.replace(
                                          /^https?:\/\//,
                                          '',
                                      )}/gallery/${job.gallery.slug}`
                                    : ''}
                            </span>
                        </span>
                        <a
                            className="ps-pill flex flex-none items-center px-3.5 text-[12px] font-semibold text-muted-foreground no-underline hover:text-foreground"
                            href={
                                job.gallery.slug && galleryBase
                                    ? `${galleryBase}/gallery/${job.gallery.slug}`
                                    : undefined
                            }
                            target="_blank"
                            rel="noreferrer"
                        >
                            Open gallery
                        </a>
                    </div>
                ) : (
                    <div className="max-w-[560px] rounded-2xl border border-dashed border-border p-4 text-[12.5px] text-muted-foreground">
                        No shared gallery — each activation keeps its own.
                        A job-level gallery can be created from the{' '}
                        <a
                            className="font-semibold text-brand-ink"
                            href={`/jobs/${id}`}
                        >
                            classic job page
                        </a>
                        .
                    </div>
                )}
            </section>

            {/* client */}
            <section className="mb-9">
                <div className="ps-label mb-1">Client</div>
                {contacts.length === 0 && (
                    <div className="text-[12.5px] text-faint">
                        No contacts linked on this job's activations yet.
                    </div>
                )}
                {contacts.map((c: any) => (
                    <div
                        key={c.id}
                        className="flex items-center gap-3 border-t border-line py-[9px] text-[12.5px] first:border-t-0"
                    >
                        <span className="w-[170px] flex-none truncate font-semibold">
                            {c.name}
                        </span>
                        <span className="w-[90px] flex-none text-muted-foreground">
                            {String(c.role ?? '').replaceAll('_', ' ')}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-faint">
                            {[c.email, c.phone, c.companyName]
                                .filter(Boolean)
                                .join(' · ')}
                        </span>
                        {c.attioId && (
                            <a
                                className="flex-none text-[12px] font-semibold text-brand-ink"
                                href={`https://app.attio.com/smilebooth/people/record/${c.attioId}/overview`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Attio ↗
                            </a>
                        )}
                    </div>
                ))}
            </section>
        </main>
    );
}
